import React from 'react'
import styled, { css } from 'styled-components'
import { fontSizes, colors } from '../../../styles/theme'
import { FaCalendarAlt } from 'react-icons/fa'
import { useEvent } from '../event-details'
import { SidebarButtonLink, SidebarButton } from './sidebar-button'
import SidebarSectionLabel from './sidebar-section-label'
import { EventStatus, eventStatusToDisplayName } from '../../../models/event/event-status'
import BoxOfficeButtonText from '../../box-office-button/box-office-button-text'

const Performance = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: ${props => props.theme.spacing.base300};
  font-size: ${fontSizes.size500};
  line-height: ${fontSizes.lineHeight600};
  padding: ${props => props.theme.spacing.base300} 0;
  color: ${props => props.theme.colors.typography.dark.subtext};

  &:not(:last-of-type) {
    border-bottom: 1px solid ${props => props.theme.colors.separator};
  }

  &:first-of-type {
    padding-top: 0;
  }
`

const PerformanceList = styled.ol`
  list-style: none;
  padding: 0;
  margin-top: 0;
`

const CancelledButton = styled(SidebarButton)`
  &,
  &:disabled {
    background-color: ${colors.alert};
    color: ${colors.white};

    &:hover {
      background-color: ${colors.alert};
    }
  }
`

const StartTimeText = styled.span`
  ${({ strikethrough }: { strikethrough: boolean }) =>
    strikethrough &&
    css`
      text-decoration: line-through;
    `}
`

const EventPerformances = () => {
  const event = useEvent()
  if (!event) return null

  return (
    <section>
      <SidebarSectionLabel>{event.getPerformanceLabel()}</SidebarSectionLabel>

      <PerformanceList>
        {event.performances.map((performance, index) => {
          return (
            <Performance key={index}>
              <FaCalendarAlt title="Time" />
              <div>
                {performance.label && (
                  <>
                    <strong>{performance.label}</strong>
                    <br />
                  </>
                )}
                <StartTimeText strikethrough={event.status !== EventStatus.ACTIVE}>
                  {performance.startTime.format}
                </StartTimeText>
              </div>
            </Performance>
          )
        })}
      </PerformanceList>

      {event.status === EventStatus.ACTIVE ? (
        event.ticketed &&
        (event.boxOfficeUrl ? (
          !event.hasPassed ? (
            <SidebarButtonLink as="a" href={event.boxOfficeUrl} rel="noopener">
              <BoxOfficeButtonText text={event.boxOfficeButtonAppearance.activeText} />
            </SidebarButtonLink>
          ) : (
            <SidebarButton disabled>
              <BoxOfficeButtonText text={event.boxOfficeButtonAppearance.eventPassedText} />
            </SidebarButton>
          )
        ) : (
          <SidebarButton disabled>
            <BoxOfficeButtonText text={event.boxOfficeButtonAppearance.noBoxOfficeUrlText} />
          </SidebarButton>
        ))
      ) : (
        <CancelledButton disabled>
          Event <b>{eventStatusToDisplayName(event.status)}</b>
        </CancelledButton>
      )}

      {event.programUrl && (
        <SidebarButtonLink
          as="a"
          href={event.programUrl}
          target="_blank"
          rel="noopener"
          variant="outlined"
        >
          View <b>Program</b>
        </SidebarButtonLink>
      )}
    </section>
  )
}

export default EventPerformances
